<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{
      zIndex: '1100',
    }"
    @hide="handleHideLookModal"
    :pt="{
      root: {
        class: 'look-modal-root'
      },
    }"
  >
    <template #container="{ closeCallback }">
      <div
        class="look-modal-container"
        :style="{
          background:
            (currentStep?.type === 'welcome' && !isDesktop)
              ? `linear-gradient(180deg,rgba(171, 183, 246, 0.5) 0%,rgba(208, 203, 255, 0) 100%)`
              : `#FFFFFF`,
          backgroundSize: 'auto 50%',
          backgroundPosition:'top center',
          backgroundRepeat:'no-repeat',
        }"
      >
        <!-- Loading State -->
        <div v-if="lookStore.lookModal.isLoadingConfig" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Загрузка опроса...</p>
        </div>

        <template v-else>
          <!-- Header -->
          <div class="look-header">
            <div class="look-header-top">
              <div class="look-header-left">
                <custom-button
                  @click="closeCallback"
                  class="save-exit-button"
                  severity="info"
                  v-if="currentStep?.id !== 'completion-1'"
                >
                  <i class="pi pi-chevron-left"></i>
                  {{ isWelcomeStep ? "Назад" : "Сохранить и выйти" }}
                </custom-button>
              </div>

              <div class="look-header-center">
                <div v-if="isWelcomeStep" class="look-title title">
                  {{ configTitle }}
                </div>
              </div>

              <div class="look-header-right">
                <custom-button
                
                  @click="closeCallback"
                  class="save-exit-button-right "
                  severity="info"
                >
                  {{ isWelcomeStep ? "" : "Сохранить и выйти" }}
                  <i class="pi pi-times"></i>
                </custom-button>
              </div>
            </div>
            <div class="look-header-bottom">
              <div v-if="!isWelcomeStep && currentStep" class="progress-container">
                <div v-if="currentStep?.id !== 'completion-1'"
                 class="progress-bar">
                  <div
                    class="progress-fill"
                    :style="{ width: `${progressPercentage}%` }"
                  ></div>
                </div>
                <span 
                v-if="currentStep?.id !== 'completion-1'"

                class="progress-text helper-text">{{ currentStepIndex + 1 }} из {{ totalSteps }}</span>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="look-content">
            <DynamicStepRenderer />
          </div>

          <!-- Footer -->
          <div v-if="showFooter" class="look-footer">
            <div class="footer-buttons" :class="currentStep?.id ? `${currentStep.id}-footer-buttons` : ''">
              <!-- For tinder_like step: Show "Данные сохранены" -->
              <template v-if="currentStep?.type === 'tinder_like'">
                <div class="data-was-saved">
                  <span class="data-was-saved-text">Данные сохранены</span>
                  <i class="pi pi-bookmark" style="font-size: 16px; color: var(--active-100);"></i>
                </div>
              </template>
              
              <!-- For other steps: Back and Next buttons -->
              <template v-else>
                <custom-button
                  v-if="showBackButton"
                  @click="prevStep"
                  :severity="isDesktop ? 'info' : 'danger'"
                  rounded
                  class="back-btn"
                  label="Назад"
                />
                <custom-button
                  v-if="showNextButton"
                  @click="handleNext"
                  class="next-btn"
                  :label="nextButtonLabel"
                  :loading="lookStore.lookModal.isLoading"
                  rounded
                />
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";
import Dialog from "primevue/dialog";

import CustomButton from "@/components/UI/CustomButtom/CustomButton.vue";

import { useLookModal } from "@/features/look/store";
import DynamicStepRenderer from "./LookSteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const lookStore = useLookModal();
const lookModal = computed(() => lookStore.lookModal);
const currentStep = computed(() => lookStore.currentStep);
const currentStepIndex = computed(() => lookStore.lookModal.currentStepIndex);
const totalSteps = computed(() => lookStore.totalSteps);
const { isDevice } = useDevice()
const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);

const configTitle = computed(() => {
  return lookStore.lookModal.config?.title || "Подбор образа";
});

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((currentStepIndex.value + 1) / totalSteps.value) * 100;
});

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide back button on welcome step
  if (stepType === "welcome") return false;
  
  // Hide back button on completion step
  if (stepType === "completion") return false;
  
  // Hide back button on steps that handle navigation themselves
  if (stepType === "tinder_like") return false;
  
  return currentStepIndex.value > 0;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide next button on completion step
  if (stepType === "completion") {
    return false;
  }
  
  // Hide next button on steps that handle navigation themselves
  if (stepType === "tinder_like") {
    return false;
  }
  
  return currentStepIndex.value < totalSteps.value - 1;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === "welcome") return "Начать тест";
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  if (stepType === "completion") {
    return false;
  }
  
  return true;
});

function handleHideLookModal() {
  lookStore.hideLookModal();
}

function prevStep() {
  lookStore.prevStep();
}

function handleNext() {
  if (!lookStore.validateCurrentStep()) {
    return;
  }
  
  lookStore.nextStep();
}

watch(
  () => lookModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
  }
);
</script>

<style>
.look-modal-root {
  width: 60vw;
  max-width: 1020px;
  height: auto;
  max-height: 90vh;
  overflow: auto;
}


@media (max-width: 1024px) {
  .look-modal-root {
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
  }
}
</style>

<style scoped>
.look-modal-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.look-title {
  display: none;
}

.look-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  padding:20px 20px 0 20px;
}

.look-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.look-header-bottom {
  width: 100%;
}

.look-header-left {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;

}
.look-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}

.look-header-center {
  flex: 1;
  text-align: center;
}

.save-exit-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  color: var(--active-100);
  cursor: pointer;
  transition: background-color 0.2s;
}


.save-exit-button-right {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 0;
  gap: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.progress-container {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4px;
  align-items: flex-start;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: var(--light-blue-20);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--active-100);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--active-100);
}

.look-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  align-self: stretch;
  
}

.look-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  padding: 20px 40px 40px 40px;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.data-was-saved {
  display: flex;
  padding: 0;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.data-was-saved-text {
  display: flex;
  width: 160px;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-XS, 4px);
  align-self: stretch;
  color: var(--active-100);
}

@media (max-width: 1024px) {
  .look-modal-container {
    display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
  }

  .back-btn {
  flex: 1;
}

.next-btn {
  flex: 1;
}

  .look-header {
    padding: 20px 20px 0 20px;
  }

  .look-title {
    display: block;
    white-space: nowrap;
  }

  .look-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
  }

  .look-header-bottom {
    margin-top:30px;
  }

  .look-footer {
    flex:none;
    padding: 0 20px 20px 20px;
  }

  .footer-buttons {
    width: 100%;
  }

  .look-header-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
    flex: 1 0 0;
    white-space: nowrap;
  }

.look-header-right {
  display: flex;
  height: 45px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 10px;
  flex: 1 0 0;
}


.save-exit-button-right {
  display: none;
}
}
</style>

